import { safeFetchAPI } from '@/api';
import { adminUtils, userUtils, utils } from '@/helpers';
import { Campaign, CampaignChannelType, FullMessagePreview, PushTemplate, SMSTemplate } from '@/types';
import { isEqual } from 'lodash';
import { create } from 'zustand';

export type TextPreviewStoreState = {
	loading: boolean;
	preview: FullMessagePreview | undefined;
	previousPreviewContent: Partial<Record<CampaignChannelType, Partial<Campaign>>> | undefined;
	debounceTimeouts: Partial<Record<CampaignChannelType, NodeJS.Timeout | undefined>> | undefined;
};

export type TextPreviewStoreActions = {
	loadPreview: (campaign: Partial<Campaign>, channel?: CampaignChannelType) => void;
	reset: () => void;
};

export type TextPreviewStore = TextPreviewStoreState & TextPreviewStoreActions;

const initialState: TextPreviewStoreState = {
	preview: undefined,
	previousPreviewContent: {},
	debounceTimeouts: {},
	loading: false,
};

export const useTextPreviewStore = create<TextPreviewStore>((set, get) => ({
	...initialState,

	loadPreview: (campaign: Partial<Campaign>, channel = CampaignChannelType.Text) => {
		set({ loading: true });

		const { debounceTimeouts = {} } = get();
		const debounceTimeout = debounceTimeouts[channel];
		// Clear any existing timeout
		if (debounceTimeout) {
			clearTimeout(debounceTimeout);
		}

		const cleanCampaign = (campaign: Partial<Campaign>) => {
			let templates: SMSTemplate | undefined;
			if (channel === CampaignChannelType.Text) {
				templates = JSON.parse(JSON.stringify(campaign?.templates || {}));
				// delete templates.dynamicContent
				// IF type is HTML and json is empty, set it to a div
				if (templates && templates.landingType === 'HTML' && !templates.landingHTML && !templates.landingDesign) {
					templates.landingHTML = '<div/>';
					templates.landingDesign = {};
				}
			}

			let pushTemplate: PushTemplate | undefined;
			if (channel === CampaignChannelType.Push) {
				pushTemplate = JSON.parse(JSON.stringify(campaign?.pushTemplate || {}));
			}

			return {
				...campaign,
				templates,
				emailTemplate: undefined,
				browserTemplate: undefined,
				pushTemplate,
				snailMailTemplate: undefined,
				adTemplate: undefined,
			};
		};

		// If previous preview content matches the campaign, don't load preview
		const previousPreviewContent = get().previousPreviewContent ?? {};
		const previousCampaign = previousPreviewContent[channel || CampaignChannelType.Text];
		if (previousCampaign && isEqual(previousCampaign, cleanCampaign(campaign))) {
			set({ loading: false });
			return;
		}

		// Set new timeout
		const timeout = setTimeout(async () => {
			set({ loading: true });
			const [nextPreview, error] = await safeFetchAPI<FullMessagePreview>('/campaign/full/preview/:uid', {
				method: 'POST',
				payload: cleanCampaign(campaign),
			});

			if (error) {
				if (adminUtils.canSeeAll()) {
					utils?.toaster?.addToast?.({
						message: `Preview error: ${error}`,
						type: 'warning',
					});
				}
				return;
			}

			const previousPreviewContent = get().previousPreviewContent ?? {};
			previousPreviewContent[channel || CampaignChannelType.Text] = campaign;

			const preview = get().preview ?? {};
			if (channel === CampaignChannelType.Push) {
				preview.push = nextPreview?.push;
			} else {
				preview.text = nextPreview?.text;
			}

			set({
				preview,
				previousPreviewContent,
				debounceTimeouts: { ...debounceTimeouts, [channel]: undefined },
				loading: false,
			});
		}, 1000); // 1 second debounce

		set({ debounceTimeouts: { ...debounceTimeouts, [channel]: timeout } });
	},

	reset: () => {
		const { debounceTimeouts = {} } = get();
		for (const timeout of Object.values(debounceTimeouts)) {
			if (timeout) {
				clearTimeout(timeout);
			}
		}
		set(initialState);
	},
}));
